<script setup lang="ts">
import { VButton, VLoadingSection } from '@/modules/shared/components'
import { useVuelidate } from '@vuelidate/core'
import { computed, ref, watch } from 'vue'
import VInputRadio from '../v-input-radio.vue'
import { required } from '@vuelidate/validators'
import AccreditationBanner from '@/modules/screening/component/AccreditationBanner.vue'

const props = defineProps<{
  individual: any
  inDisplayMode?: boolean
  isWizard?: boolean
  onSubmit: (payload: any) => Promise<void>
}>()

const isWizard = computed(() => !!props.isWizard)
const isFieldDisabled = computed(() => !!props.inDisplayMode)
const currentIndividual = computed(() => props.individual)
const skeleton = computed(() => !currentIndividual.value)
const accreditation = computed(() => currentIndividual.value?.accreditation)
const accreditationTemplate = computed(() => accreditation.value?.template)

///////////////////////////////////////////////////////////////////////////////
// Options
///////////////////////////////////////////////////////////////////////////////

const generateAccreditationOptions = (section) => {
  return section.options.map((item) => {
    return {
      label: item.option,
      value: item.id,
    }
  })
}

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////

const accreditationInitialState = {
  response_id: null,
}
const accreditationForm = ref({ ...accreditationInitialState })
const accreditationRules = {
  response_id: { required },
}
const v$ = useVuelidate(accreditationRules, accreditationForm, { $lazy: true })

const setForm = () => {
  accreditationForm.value.response_id = accreditation.value?.response_id
}

const resetForm = () => {
  v$.value.$reset()
  setForm()
}

watch(currentIndividual, (value) => {
  if (value) setForm()
})

const isValid = async () => {
  const valid = await v$.value.$validate()
  return valid
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return
  const payload = { ...accreditationForm.value }
  await props.onSubmit(payload)
}

defineExpose({
  isValid,
  resetForm,
  submit,
})
</script>

<template>
  <div class="w-full rounded-lg border-[1px] p-5">
    <div class="mb-5 flex items-center justify-between">
      <div class="text-lg font-semibold">Accreditation Status</div>
      <div class="flex items-center gap-3">
        <slot name="actions"></slot>
      </div>
    </div>
    <div class="space-y-6" v-if="!skeleton">
      <AccreditationBanner v-if="isFieldDisabled && !isWizard" :status="accreditation.status" />
      <div class="flex flex-col gap-10">
        <VInputRadio
          v-for="section in accreditationTemplate"
          :key="section.title"
          :label="section.title"
          v-model="accreditationForm.response_id"
          :options="generateAccreditationOptions(section)"
          property="response_id"
          aligned="vertical"
          :v$="v$"
          :disabled="isFieldDisabled"
        />
      </div>
    </div>
    <VLoadingSection
      v-else
      class="my-40"
      title="Preparing Your Information"
      description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
    smoothly."
    />
  </div>
  <div class="mt-24" v-if="!isFieldDisabled && !isWizard">
    <VButton variant="v-blue" size="lg" :click="submit">Confirm Changes</VButton>
  </div>
</template>
